/**
 * Getting back to the saloon after the connection drops.
 *
 * The server holds a game for a few minutes after its player disappears, so
 * the app retries on its own for about that long, backing off as it goes. Past
 * that the game is gone anyway, and the banner leaves the next try to the
 * player. The resume token is kept in session storage, so reloading the tab
 * mid-game still finds the way back to the table.
 */

import type { ConnectionStatus } from "./connection.js";

/** Pauses between automatic attempts, adding up to a little under the server's hold. */
export const RETRY_DELAYS_MS: readonly number[] = [
    1000,
    2000,
    4000,
    8000,
    15_000,
    30_000,
    30_000,
    30_000,
    30_000,
    30_000,
    30_000,
    30_000
];

const RESUME_TOKEN_KEY = "haf-saloon-resume-token";

export interface ConnectionState {
    readonly status: ConnectionStatus;
    /** True once the connection has dropped and not yet come back. */
    readonly lost: boolean;
    /** True while another automatic attempt is still to come. */
    readonly retrying: boolean;
    /** True when a table is holding a seat on the server for this player. */
    readonly holdingGame: boolean;
    /** Something to tell the player once they are back, such as a game that expired. */
    readonly notice: string | null;
}

export interface BannerModel {
    readonly message: string;
    readonly action: string;
    readonly actionDisabled: boolean;
}

/** The pause before the given attempt, or null once the app should stop trying on its own. */
export function getRetryDelay(attempt: number): number | null {
    return RETRY_DELAYS_MS[attempt] ?? null;
}

/** What the connection banner says and offers, or null when it should be hidden. */
export function describeConnection(state: ConnectionState): BannerModel | null {
    if (state.status === "open" || !state.lost) {
        return state.notice === null ? null : { message: state.notice, action: "Dismiss", actionDisabled: false };
    }

    if (state.status === "replaced") {
        return {
            message: "This game was picked up in another tab or window.",
            action: "Play here instead",
            actionDisabled: false
        };
    }

    const held = state.holdingGame ? " Your seat is being held for a few minutes." : "";

    if (state.status === "connecting" || state.retrying) {
        return {
            message: `Lost the connection to the saloon. Reconnecting…${held}`,
            action: "Reconnect now",
            actionDisabled: state.status === "connecting"
        };
    }

    return {
        message: `Can't reach the saloon. Check that the server is running, then try again.${held}`,
        action: "Try again",
        actionDisabled: false
    };
}

/** Session storage can be off or full; losing the token only costs a reload's resume. */
export function loadResumeToken(): string | null {
    try {
        return sessionStorage.getItem(RESUME_TOKEN_KEY);
    }
    catch {
        return null;
    }
}

export function saveResumeToken(token: string | null): void {
    try {
        if (token === null) {
            sessionStorage.removeItem(RESUME_TOKEN_KEY);
        }
        else {
            sessionStorage.setItem(RESUME_TOKEN_KEY, token);
        }
    }
    catch {
        // See loadResumeToken: the in-memory copy still covers a dropped socket.
    }
}
